import { z } from 'zod'
import { oc } from '@orpc/contract'

// Mirrors the model's agenda value sets (src/model/appointment/agenda-types.ts; the insurance ones
// come from src/model/eligibility/types.ts). Inlined because this contract module is bundled into
// the frontends via @behuman/core-contract; transitive imports from the model layer would drag in
// tsconfig path aliases the Vite build can't resolve.
export const AGENDA_APPOINTMENT_STATUSES = [
  'pending',
  'booked',
  'checked-in',
  'arrived',
  'fulfilled',
  'noshow',
] as const
export const AGENDA_ENCOUNTER_STATUSES = [
  'planned',
  'arrived',
  'triaged',
  'in-progress',
  'onleave',
  'finished',
  'cancelled',
  'entered-in-error',
  'unknown',
] as const
export const AGENDA_ELIGIBILITY_STATUSES = [
  'verified',
  'approved-manually',
  'disapproved',
  'not-verified',
  'none',
] as const
export const AGENDA_CLINIC_DECISIONS = ['approve', 'disapprove'] as const
export const AGENDA_INSURANCE_STATUSES = ['verified', 'review-required', 'none'] as const
export const AGENDA_READINESS = [
  'ready',
  'in-progress',
  'completed',
  'no-show',
  'upcoming',
] as const
export const AGENDA_VISIT_REASON_SOURCES = ['patient', 'staff'] as const
export const AGENDA_ALLERGY_CRITICALITIES = ['low', 'high', 'unable-to-assess'] as const
export const AGENDA_VISIT_STATES = [
  'upcoming',
  'ready',
  'in-call',
  'generating',
  'note-ready',
  'finished',
] as const

// Longest window one call may cover — the provider portal's Home fetches a whole month for
// its calendar dots; anything wider is a report, not an agenda.
export const AGENDA_MAX_WINDOW_DAYS = 31
const AGENDA_MAX_WINDOW_MS = AGENDA_MAX_WINDOW_DAYS * 24 * 60 * 60 * 1000

const participantSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
})

export const agendaAppointmentSchema = z.object({
  appointmentId: z.string(),
  encounterId: z.string().nullable(),
  appointmentStatus: z.enum(AGENDA_APPOINTMENT_STATUSES),
  encounterStatus: z.enum(AGENDA_ENCOUNTER_STATUSES).nullable(),
  start: z.string().nullable(),
  end: z.string().nullable(),
  durationMinutes: z.number().int().nullable(),
  patient: participantSchema
    .extend({
      birthday: z.string().nullable(),
      // Whole years as of the window's `from` instant (UTC calendar).
      age: z.number().int().nullable(),
      // Presigned, expiring URL of the first Patient.photo — loadable without credentials.
      photoUrl: z.string().nullable(),
      // Current allergies (active or unstated clinical status, not refuted / entered-in-error) —
      // the visit screen's safety alert. `criticality` follows AllergyIntolerance.criticality.
      allergies: z.array(
        z.object({
          display: z.string().nullable(),
          code: z.string().nullable(),
          criticality: z.enum(AGENDA_ALLERGY_CRITICALITIES).nullable(),
        }),
      ),
    })
    .nullable(),
  practitioner: participantSchema.nullable(),
  visitReason: z
    .object({
      code: z.string().nullable(),
      label: z.string().nullable(),
      source: z.enum(AGENDA_VISIT_REASON_SOURCES).nullable(),
    })
    .nullable(),
  insurance: z.object({
    payorName: z.string().nullable(),
    plan: z.string().nullable(),
    // Payer eligibility of the attached coverage (model/eligibility).
    payerEligibility: z.enum(AGENDA_ELIGIBILITY_STATUSES),
    /** @deprecated Same value as `payerEligibility`; remove once mobile stops reading it. */
    eligibility: z.enum(AGENDA_ELIGIBILITY_STATUSES),
    clinicDecision: z.enum(AGENDA_CLINIC_DECISIONS).nullable(),
    // Roll-up: `verified` when eligibility is verified/approved-manually and the clinic did not
    // disapprove; `review-required` for any other attached coverage; `none` without coverage.
    status: z.enum(AGENDA_INSURANCE_STATUSES),
  }),
  // Booking confirmed: status booked or later (checked-in, arrived, fulfilled).
  confirmed: z.boolean(),
  // ready = patient checked in (the Join gate); in-progress / completed follow the encounter;
  // no-show; upcoming otherwise.
  readiness: z.enum(AGENDA_READINESS),
  // A current, non-imported Progress Note exists for the encounter.
  hasProgressNote: z.boolean(),
  // That note is signed (docStatus final or amended).
  progressNoteLocked: z.boolean(),
  // The visit card's one action, readiness overlaid with live scribe / generation state:
  // generating (draft in flight) > note-ready (note exists) > in-call (encounter in progress or a
  // scribe session is live — Rejoin) > finished (completed / no-show) > ready (Join) > upcoming.
  visitState: z.enum(AGENDA_VISIT_STATES),
})

// Model-layer paging (`ModelPageRequest`): `offset` is the first row, `count` caps how many,
// `limit` is a hard ceiling on the row index. Optional on input — an agenda is normally read whole.
const pageRequestSchema = z.object({
  offset: z.number().int().min(0),
  count: z.number().int().min(0),
  limit: z.number().int().min(0),
})

const pageSchema = pageRequestSchema.extend({
  size: z.number().int().min(0),
  total: z.number().int().min(0),
  // Set when the window held more rows than Medplum's offset ceiling; `total` is then a lower bound.
  truncated: z.boolean().optional(),
})

/**
 * A provider's agenda: every non-cancelled appointment starting in [from, to] for the caller
 * (or, for care coordinators, for the requested in-org practitioners), as flat rows with the
 * patient, encounter, visit reason and insurance verdicts the appointments screen renders.
 *
 * Appointment-based: an Encounter without an Appointment is not an agenda entry. Unpaged —
 * the page envelope reports the true size. The client owns the local-day → instant conversion
 * (`from`/`to` are instants; the server is timezone-agnostic).
 */
export const listAppointmentAgenda = oc
  .route({ method: 'GET', path: '/v2/appointments/agenda' })
  .input(
    z
      .object({
        from: z.iso.datetime({ offset: true }),
        to: z.iso.datetime({ offset: true }),
        // Defaults to the caller. Every id must belong to the caller's organization.
        practitionerIds: z.array(z.string().min(1)).max(100).optional(),
        // Omit for the whole window (the default for a day view); the response echoes the
        // effective request either way.
        page: pageRequestSchema.optional(),
      })
      .refine((input) => Date.parse(input.to) > Date.parse(input.from), {
        message: '`to` must be after `from`',
        path: ['to'],
      })
      .refine((input) => Date.parse(input.to) - Date.parse(input.from) <= AGENDA_MAX_WINDOW_MS, {
        message: `window may not exceed ${AGENDA_MAX_WINDOW_DAYS} days`,
        path: ['to'],
      }),
  )
  .output(
    z.object({
      page: pageSchema,
      data: z.array(agendaAppointmentSchema),
    }),
  )
